import { clientPortalApi } from '@/lib/api/client-portal.api'
import { z } from 'zod'

import type { PaginatedResponse, ThreadComment } from '@scrambl/shared'

import type { PortalMetadataContext } from './metadata.api'
import { PortalMetadataContractError } from './metadata.api'

const receiptSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  threadId: z.string().uuid(),
  parentCommentId: z.string().uuid().nullable(),
  page: z.number().int().positive(),
  limit: z.number().int().min(1).max(100),
  observedAt: z.string().datetime(),
  commentIds: z.array(z.string().uuid()).max(100),
  isInternalViewer: z.boolean(),
})
export type PortalCommentReceipt = z.infer<typeof receiptSchema>
export type PortalCommentPage = PaginatedResponse<ThreadComment> & {
  portalCommentReceipt?: PortalCommentReceipt
}
const fail = (): never => {
  throw new PortalMetadataContractError()
}
export function parsePortalCommentReceipt(raw: unknown): PortalCommentReceipt {
  const result = receiptSchema.safeParse(raw)
  if (
    !result.success ||
    new Set(result.data.commentIds).size !== result.data.commentIds.length
  )
    fail()
  return result.data
}
export function assertPortalCommentPage(
  page: PortalCommentPage,
  context: {
    userId?: string
    organizationId: string
    portalEntityId: string | null
  },
  params: {
    threadId: string
    parentCommentId?: string | null
    page?: number
    limit?: number
  },
): PortalCommentReceipt {
  const receipt = parsePortalCommentReceipt(page.portalCommentReceipt)
  const meta = page.meta,
    limit = params.limit ?? 10,
    requestedPage = params.page ?? 1
  if (
    receipt.userId !== context.userId ||
    receipt.organizationId !== context.organizationId ||
    receipt.entityId !== context.portalEntityId ||
    receipt.threadId !== params.threadId ||
    receipt.parentCommentId !== (params.parentCommentId ?? null) ||
    receipt.page !== requestedPage ||
    receipt.limit !== limit ||
    !meta ||
    meta.itemsPerPage !== limit ||
    meta.currentPage !== requestedPage ||
    meta.totalPages !== Math.ceil(meta.totalItems / limit) ||
    !Number.isSafeInteger(meta.totalItems) ||
    meta.totalItems < 0 ||
    meta.hasNextPage !== requestedPage * limit < meta.totalItems ||
    !Array.isArray(page.data) ||
    page.data.length !==
      Math.min(
        limit,
        Math.max(0, meta.totalItems - (requestedPage - 1) * limit),
      ) ||
    receipt.commentIds.length !== page.data.length ||
    receipt.commentIds.some((id, index) => id !== page.data[index]?.id)
  )
    fail()
  return receipt
}
export const portalDiscussionReadApi = {
  async mark(context: PortalMetadataContext, original: PortalCommentReceipt) {
    context.signal.throwIfAborted()
    const receipt = parsePortalCommentReceipt(original)
    if (
      receipt.userId !== context.userId ||
      receipt.entityId !== context.entityId ||
      receipt.parentCommentId !== null ||
      receipt.isInternalViewer
    )
      fail()
    const result = await clientPortalApi.markThreadRead(
      context.entityId,
      receipt.threadId,
      {
        seenCommentIds: [...receipt.commentIds],
        observedAt: receipt.observedAt,
      },
      { signal: context.signal },
    )
    context.signal.throwIfAborted()
    if (
      !result ||
      result.userId !== receipt.userId ||
      result.organizationId !== receipt.organizationId ||
      result.entityId !== receipt.entityId ||
      result.recordType !== 'THREAD' ||
      result.recordId !== receipt.threadId ||
      !Number.isSafeInteger(result.updated) ||
      result.updated < 0
    )
      fail()
    return { updated: result.updated }
  },
}
